import * as THREE from "three";
import TWEEN from "@tweenjs/tween.js";
import { TweenManager } from "../core/TweenManager.js";
import { store } from "../core/store.js";
import { models } from "../models/models.js";
import { notifications } from "../ui/notifications.js";

var planesByID = {}; // Object with keys indexed to ID of Plane from scene.json file
var activePlanes = []; // Flat list of active planes IDs
var uiUpdateCallback = null;
var modelsClipboard = [];

var controller = { public: {} };

// Bounds checking helper
function clampPlaneToSceneBounds(planePosition) {
  const bounds = new THREE.Box3().setFromObject(store.scene);
  const maxDistance = bounds.getSize(new THREE.Vector3()).length();

  return {
    ...planePosition,
    c: Math.min(Math.max(-maxDistance, planePosition.c), maxDistance),
  };
}

function toggleLocalClipping() {
  // Enables/Disables the localClipping as needed
  if (activePlanes.length > 0 && store.webglRenderer.localClippingEnabled === false) {
    store.webglRenderer.localClippingEnabled = true;
  } else if (activePlanes.length === 0 && store.webglRenderer.localClippingEnabled === true) {
    store.webglRenderer.localClippingEnabled = false;
  }
}

function generatePlane(position) {
  // Ensure normal is normalized
  const normal = new THREE.Vector3(position.x, position.y, position.z).normalize();
  var plane = new THREE.Plane(normal, position.c);
  return plane;
}

function generateHelper(plane) {
  const helperSize = calculateHelperSize();
  let helper = new THREE.PlaneHelper(plane, helperSize, 0xffff00);
  helper.children[0].material.opacity = 0.8;
  return helper;
}


function calculateHelperSize() {
  const box = new THREE.Box3().setFromObject(store.scene);
  const size = box.getSize(new THREE.Vector3());
  return Math.max(size.x, size.y, size.z) * 2;
}

function findUnusedID() {
  let idFound = false;
  let id = 1;
  while (!idFound) {
    if (planesByID[id] === undefined) {
      idFound = true;
    } else {
      id++;
    }
  }
  return id;
}

function convertToSpherical(position) {
  const spherical = new THREE.Spherical().setFromCartesianCoords(position.x, position.y, position.z);
  return {
    phi: Math.round(THREE.MathUtils.radToDeg(spherical.phi)),
    theta: Math.round(THREE.MathUtils.radToDeg(spherical.theta)),
    distance: position.c,
  };
}

controller.loadPlane = function loadPlane(data) {
  if (Object.prototype.hasOwnProperty.call(planesByID, data.id)) {
    console.log("Duplicate Plane Found! Ignoring");
    return;
  }

  const cp = Object.assign({}, data);
  cp.startPosition = Object.assign({}, cp.startPosition);

  if (cp.endPosition !== null) {
    cp.endPosition = Object.assign({}, cp.endPosition);
  }

  cp.startPosition = clampPlaneToSceneBounds(cp.startPosition);
  if (cp.endPosition) {
    cp.endPosition = clampPlaneToSceneBounds(cp.endPosition);
  }

  cp.plane = generatePlane(cp.startPosition);
  cp.helper = generateHelper(cp.plane);
  cp._previewPosition = null; // Track which position is being previewed

  // Initialize spherical coordinates
  cp.startSpherical = convertToSpherical(cp.startPosition);
  cp.endSpherical = convertToSpherical(cp.endPosition);

  planesByID[cp.id] = cp;
  store.requestRender();
};

controller.public.createPlane = function createPlane() {
  const cp = {
    id: findUnusedID(),
    startPosition: { x: 0, y: 0, z: 1, c: 0 },
    endPosition: { x: 0, y: 0, z: 1, c: 0 },
    duration: 0,
    models: [],
    name: "Untitled Clipping Plane",
    _previewPosition: null,
  };
  cp.plane = generatePlane(cp.startPosition);
  cp.helper = generateHelper(cp.plane);
  cp.startSpherical = convertToSpherical(cp.startPosition);
  cp.endSpherical = convertToSpherical(cp.endPosition);
  planesByID[cp.id] = cp;
  store.requestRender();
  return cp;
};

controller.public.clonePlane = function clonePlane(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }

  const newPlane = {
    id: findUnusedID(),
    startPosition: Object.assign({}, cp.startPosition),
    endPosition: Object.assign({}, cp.endPosition),
    duration: cp.duration,
    models: cp.models.slice(),
    name: cp.name + " (Copy)",
    _previewPosition: null,
  };

  newPlane.plane = generatePlane(newPlane.startPosition);
  newPlane.helper = generateHelper(newPlane.plane);
  planesByID[newPlane.id] = newPlane;
  store.requestRender();
  return newPlane;
};

controller.public.deletePlane = function deletePlane(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }

  if (cp.helper && store.scene) {
    store.scene.remove(cp.helper);
  }

  controller.public.deactivate(cp.id);
  delete planesByID[id];
};

controller.public.copyModels = function copyModels(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }
  modelsClipboard = cp.models.slice();
  notifications.add({ content: "Models copied to clipboard", displayTime: 2000 });
};

controller.public.pasteModels = function pasteModels(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }
  cp.models = modelsClipboard.slice();
  notifications.add({ content: "Models pasted", displayTime: 2000 });
};

controller.batchActivate = function batchActivate(idArr) {
  idArr.forEach((id) => {
    controller.public.activate(id);
  });

  if (uiUpdateCallback) {
    uiUpdateCallback();
  }
};

controller.public.editPosition = function editPosition(id, mode, values) {

  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }

  controller.initialize(id);

  if (!cp.helper.parent) {
    store.scene.add(cp.helper);
  }

  const s = new THREE.Spherical(1, degToRad(values.phi), degToRad(values.theta));
  let unitVec = new THREE.Vector3().setFromSpherical(s);

  if (mode === "START") {
    cp.startPosition.x = unitVec.x;
    cp.startPosition.y = unitVec.y;
    cp.startPosition.z = unitVec.z;
    cp.startPosition.c = values.distance;

    // Update cached spherical coordinates
    cp.startSpherical = { phi: values.phi, theta: values.theta, distance: values.distance };

    // Always update the plane to reflect the new values
    controller.resetStart(id);
  } else if (mode === "END") {
    cp.endPosition.x = unitVec.x;
    cp.endPosition.y = unitVec.y;
    cp.endPosition.z = unitVec.z;
    cp.endPosition.c = values.distance;

    // Update cached spherical coordinates
    cp.endSpherical = { phi: values.phi, theta: values.theta, distance: values.distance };

    // Always update the plane to reflect the new values
    controller.resetEnd(id);
  }

  // Ensure the view updates with the new plane values
  store.requestRender();
};

controller.initialize = function initialize(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }

  if (activePlanes.indexOf(id) === -1) {
    cp.models.map((modelID) => {
      const model = models.getModelByOriginalID(modelID);
      if (!model) {
        console.log("Problem Model");
        console.log(model);
        console.log(modelID);
        return;
      }
      if (Array.isArray(model.material.clippingPlanes)) {
        model.material.clippingPlanes.push(cp.plane);
      } else {
        model.material.clippingPlanes = [cp.plane];
      }
    });
    activePlanes.push(id);
    toggleLocalClipping();
  }

  store.requestRender();
};

controller.public.removeHelper = function removeHelper(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }
  store.scene.remove(cp.helper);
  cp._previewPosition = null;
  store.requestRender();
};

controller.public.showHelper = function showHelper(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }
  store.scene.add(cp.helper);
  store.requestRender();
};

controller.public.toggleHelper = function toggleHelper(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }

  if (cp.helper.parent === store.scene) {
    store.scene.remove(cp.helper);
    cp._previewPosition = null;
  } else {
    store.scene.add(cp.helper);
  }
  store.requestRender();
};


controller.public.togglePreviewPosition = function togglePreviewPosition(id, which = "START") {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return false;
  }

  // If already previewing this position, turn it off
  if (cp._previewPosition === which) {
    store.scene.remove(cp.helper);
    cp._previewPosition = null;
    store.requestRender();
    return false;
  }

  // Initialize the plane to ensure it's applied to models
  controller.initialize(id);

  // Show helper and set plane to the selected position
  if (which === "END") {
    controller.resetEnd(id);
  } else {
    controller.resetStart(id);
  }

  cp._previewPosition = which;
  store.scene.add(cp.helper);
  store.requestRender();
  return true;
};

// Check if a position is being previewed
controller.public.isPreviewingPosition = function isPreviewingPosition(id, which) {
  const cp = planesByID[id];
  if (!cp) return false;
  return cp._previewPosition === which;
};

controller.resetStart = function resetStart(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }

  if (
    cp.plane.normal.x !== cp.startPosition.x ||
    cp.plane.normal.y !== cp.startPosition.y ||
    cp.plane.normal.z !== cp.startPosition.z ||
    cp.plane.constant !== cp.startPosition.c
  ) {
    cp.plane.normal.x = cp.startPosition.x;
    cp.plane.normal.y = cp.startPosition.y;
    cp.plane.normal.z = cp.startPosition.z;
    cp.plane.constant = cp.startPosition.c;
  }
};

controller.resetEnd = function resetEnd(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }

  if (
    cp.plane.normal.x !== cp.endPosition.x ||
    cp.plane.normal.y !== cp.endPosition.y ||
    cp.plane.normal.z !== cp.endPosition.z ||
    cp.plane.constant !== cp.endPosition.c
  ) {
    cp.plane.normal.x = cp.endPosition.x;
    cp.plane.normal.y = cp.endPosition.y;
    cp.plane.normal.z = cp.endPosition.z;
    cp.plane.constant = cp.endPosition.c;
  }
};

controller.public.activate = function activate(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad ID. Clipping Plane not found.");
    return;
  }

  if (activePlanes.indexOf(id) === -1) {
    controller.initialize(id);
  }

  // Always reset plane to start position first
  controller.resetStart(id);

  if (cp.duration === 0) {
    return;
  }

  // Copy the start and end to new objects so they don't get mutated.
  const start = Object.assign({}, cp.startPosition);
  const end = Object.assign({}, cp.endPosition);

  TweenManager.createTween(start)
    .to(end, cp.duration)
    .easing(TWEEN.Easing.Cubic.InOut)
    .onUpdate(function () {
      cp.plane.normal.set(start.x, start.y, start.z);
      cp.plane.constant = start.c;
    })
    .start();
  store.requestRender();
};

controller.deactivateAll = function deactivateAll() {
  activePlanes.slice().forEach((id) => {
    controller.public.deactivate(id);
  });
};

controller.public.deactivate = function deactivate(id) {
  if (activePlanes.indexOf(id) === -1) return;
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad clipping plane ID. Not Found");
    return;
  }
  cp.models.map((id) => {
    const model = models.getModelByOriginalID(id);
    if (!model) return;

    if (Array.isArray(model.material.clippingPlanes)) {
      const index = model.material.clippingPlanes.indexOf(cp.plane);
      if (index !== -1) {
        model.material.clippingPlanes.splice(index, 1);
      }
    }
  });

  if (cp.helper.parent && store.scene && cp.helper.parent === store.scene) {
    store.scene.remove(cp.helper);
  }

  // Clear preview state when deactivating
  cp._previewPosition = null;

  controller.resetStart(id);
  activePlanes.splice(activePlanes.indexOf(id), 1);
  toggleLocalClipping();
  if (store.requestRender) {
    store.requestRender();
  }
};

controller.resetPlane = function resetPlane(id) {
  const cp = planesByID[id];
  if (!cp) {
    console.log("Bad Plane ID");
    return;
  }
  cp.plane.normal.set(cp.startPosition.x, cp.startPosition.y, cp.startPosition.z);
  cp.plane.constant = cp.startPosition.c;
  store.requestRender();
};

controller.getPlane = function getPlane(id) {
  const cp = planesByID[id];
  if (!cp) return {};
  return cp;
};

controller.public.setStartVector = function setStartVector(id, startVec) {
  const cp = planesByID[id];
  if (!cp) return;

  const normal = new THREE.Vector3(startVec.x, startVec.y, startVec.z).normalize();
  const clamped = clampPlaneToSceneBounds({
    x: normal.x,
    y: normal.y,
    z: normal.z,
    c: startVec.c,
  });

  Object.assign(cp.startPosition, clamped);
  cp.startSpherical = null;
};

controller.public.setDuration = function setDuration(id, duration) {
  const cp = planesByID[id];
  if (!cp) return;
  cp.duration = duration;
};

controller.public.setName = function setName(id, name) {
  const cp = planesByID[id];
  if (!cp) return;
  cp.name = name;
};

controller.public.setEndVector = function setEndVector(id, endVec) {
  const cp = planesByID[id];
  if (!cp) return;

  const normal = new THREE.Vector3(endVec.x, endVec.y, endVec.z).normalize();
  const clamped = clampPlaneToSceneBounds({
    x: normal.x,
    y: normal.y,
    z: normal.z,
    c: endVec.c,
  });

  Object.assign(cp.endPosition, clamped);
  cp.endSpherical = null;
};

controller.public.addModel = function addModel(id, modelID) {
  const cp = planesByID[id];
  if (!cp) return;

  const model = models.getModelByOriginalID(modelID);
  if (!model) {
    console.log("Bad model ID. Doesn't exist");
    return;
  }

  cp.models.push(modelID);

  if (activePlanes.indexOf(id) !== -1) {
    if (Array.isArray(model.material.clippingPlanes)) {
      model.material.clippingPlanes.push(cp.plane);
    } else {
      model.material.clippingPlanes = [cp.plane];
    }
    if (model.material) {
      model.material.clipShadows = true;
    }
  }
  store.requestRender();
};

controller.public.removeModel = function removeModel(id, modelID) {
  const cp = planesByID[id];
  if (!cp) return;

  const model = models.getModelByOriginalID(modelID);
  if (!model) {
    console.log("Bad model ID. Doesn't exist");
    return;
  }

  const modelIndex = cp.models.indexOf(modelID);
  if (modelIndex === -1) {
    return;
  }

  if (activePlanes.indexOf(id) !== -1) {
    if (Array.isArray(model.material.clippingPlanes)) {
      const clippingIndex = model.material.clippingPlanes.indexOf(cp.plane);
      model.material.clippingPlanes.splice(clippingIndex, 1);
    }
  }
  cp.models.splice(modelIndex, 1);
  store.requestRender();
};

controller.public.getClippingPlanes = function getClippingPlanes() {
  const tempArr = [];
  for (var key in planesByID) {
    if (!Object.prototype.hasOwnProperty.call(planesByID, key)) continue;
    const cp = planesByID[key];

    const startPos = cp.startSpherical || convertToSpherical(cp.startPosition);
    const endPos = cp.endSpherical || convertToSpherical(cp.endPosition);

    const tSerialPlane = {
      id: cp.id,
      startPosition: startPos,
      endPosition: endPos,
      duration: cp.duration,
      models: cp.models.slice(),
      name: cp.name,
      active: activePlanes.indexOf(cp.id) !== -1,
      helperVisible: cp.helper.parent === store.scene,
      previewingStart: cp._previewPosition === "START",
      previewingEnd: cp._previewPosition === "END",
    };

    tempArr.push(tSerialPlane);
  }
  return tempArr;
};

controller.clean = function removeMissingModels() {
  Object.keys(planesByID).map((key) => {
    if (!Object.prototype.hasOwnProperty.call(planesByID, key)) return;
    const plane = planesByID[key];
    const modelsIDS = window.Inventum.models.getModelsIDS();
    plane.models = plane.models.filter((id) => modelsIDS.includes(id.toString()));
  });
};

controller.generateJSON = function generateJSON() {
  const tArray = [];
  Object.keys(planesByID).map((key) => {
    if (Object.prototype.hasOwnProperty.call(planesByID, key)) {
      const plane = planesByID[key];
      const tempPlane = {
        id: plane.id,
        startPosition: Object.assign({}, plane.startPosition),
        endPosition: Object.assign({}, plane.endPosition),
        duration: plane.duration,
        models: plane.models.slice(),
        name: plane.name,
      };
      tArray.push(tempPlane);
    }
  });
  return { clipping: tArray };
};

controller.getActive = function getActive() {
  return activePlanes.slice();
};

controller.public.getClippingPlanesIDS = function getClippingPlanesIDS() {
  return Object.keys(planesByID);
};

controller.public.setUIUpdateCallback = function setUIUpdateCallback(callback) {
  uiUpdateCallback = callback;
};

controller.reset = function reset() {
  Object.keys(planesByID).forEach((id) => {
    controller.public.deletePlane(id);
  });

  planesByID = {};
  activePlanes = [];
  modelsClipboard = [];
};

function radToDeg(radians) {
  return radians * (180 / Math.PI);
}

function degToRad(degrees) {
  return degrees * (Math.PI / 180);
}

export { controller as clipping };
